import { useState, useEffect, useCallback } from "react";

interface UseSpeechOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
}

interface UseSpeechReturn {
  speak: (text: string) => void;
  stop: () => void;
  isSpeaking: boolean;
  isSupported: boolean;
}

/**
 * Hook for text-to-speech using Web Speech API
 */
export function useSpeech(options: UseSpeechOptions = {}): UseSpeechReturn {
  const { rate = 0.9, pitch = 1, volume = 1 } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported(
      typeof window !== "undefined" && "speechSynthesis" in window,
    );
  }, []);

  useEffect(() => {
    if (!isSupported) return;

    // Cleanup: cancel any ongoing speech when component unmounts
    return () => {
      speechSynthesis.cancel();
    };
  }, [isSupported]);

  const speak = useCallback(
    (text: string) => {
      if (!isSupported) return;

      // Stop any current speech
      speechSynthesis.cancel();

      // Clean text: remove LaTeX notation for speech
      const cleanText = text
        .replace(/\$\$([^$]+)\$\$/g, "$1") // Remove display math delimiters
        .replace(/\$([^$]+)\$/g, "$1") // Remove inline math delimiters
        .replace(/\\\[([^\]]+)\\\]/g, "$1") // Remove \[...\]
        .replace(/\\\(([^)]+)\\\)/g, "$1") // Remove \(...\)
        .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold markers
        .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1 over $2") // Convert fractions
        .replace(/\\sqrt\{([^}]+)\}/g, "square root of $1") // Convert sqrt
        .replace(/\^(\d+)/g, " to the power of $1") // Convert exponents
        .replace(/\^{([^}]+)}/g, " to the power of $1") // Convert exponents with braces
        .replace(/_(\d+)/g, " sub $1") // Convert subscripts
        .replace(/_{([^}]+)}/g, " sub $1") // Convert subscripts with braces
        .replace(/\\times/g, " times ") // Convert multiplication
        .replace(/\\div/g, " divided by ") // Convert division
        .replace(/\\pm/g, " plus or minus ") // Convert plus-minus
        .replace(/\\pi/g, " pi ") // Convert pi
        .replace(/\\[a-zA-Z]+/g, " ") // Remove other LaTeX commands
        .replace(/[{}]/g, "") // Remove braces
        .replace(/\s+/g, " ") // Normalize whitespace
        .trim();

      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.volume = volume;

      // Try to use a good English voice
      const voices = speechSynthesis.getVoices();
      const preferredVoice = voices.find(
        (v) =>
          v.lang.startsWith("en") &&
          (v.name.includes("Google") ||
            v.name.includes("Natural") ||
            v.name.includes("Enhanced")),
      );
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      speechSynthesis.speak(utterance);
    },
    [isSupported, rate, pitch, volume],
  );

  const stop = useCallback(() => {
    if (!isSupported) return;
    speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [isSupported]);

  return { speak, stop, isSpeaking, isSupported };
}
